/**
 * The processing-terms card on sheet 41 (mission 7 WP09 T049; callouts 11 and
 * 12; FR-007, FR-023, FR-028, C-004; TX.3, T.1, A.5; contracts/ui-api.md
 * "Sheet 41"; research §1.10).
 *
 * The status is Not accepted, Accepted (version, when, and the tenant's
 * contact email as "who": one identity, A.3 as amended), or a new version
 * awaiting acceptance. The items render from `PROCESSING_TERMS` only, verbatim
 * (C-003, CLAUDE.md §3.5): counsel's five (memo 2026-10-03, A3), the only place
 * their legal wording appears on the page (§3.7). While the terms are an
 * engineering placeholder (their text carries `LEGAL_Q14_TAG`), the tag heads
 * the summary in every state, so the warranties are never presented as final
 * (spec US3, C-003, FR-023, SC-008, callout 41/12); counsel's text carries no tag. "Read and accept" is primary until the
 * current version is accepted, and opens the shared blocking dialog. An
 * acceptance made here turns the status into a success band that stays
 * (style guide §7.5).
 */
import { useState, type ReactNode } from 'react'

import { Band } from '@/components/trellis/Band'
import { ProcessingTermsDialog } from '@/components/trellis/ProcessingTermsDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import type { ProcessingTermsStatus } from '@/lib/api/terms'
import { date, isoDateTime, time } from '@/lib/format'
import { LEGAL_Q14_TAG, PROCESSING_TERMS } from '@/lib/mandatory-copy'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

export interface TermsCardProps {
  terms: ProcessingTermsStatus
  /** The tenant's contact email, the one identity that accepts (C-004). */
  email: string
}

/** The terms are Legal's placeholder while their text carries the Q14 tag (R22 removes it with the final wording). */
const PLACEHOLDER = PROCESSING_TERMS.text.includes(LEGAL_Q14_TAG)

const when = (iso: string) => `${date(iso)} ${time(iso)}`

function Timed({ at, children }: { at: string; children: ReactNode }) {
  return (
    <>
      <time dateTime={isoDateTime(at)}>{children}</time> <span className="text-navy-600">{copyText('timesInUtc')}</span>
    </>
  )
}

function statusLine(terms: ProcessingTermsStatus, email: string): ReactNode {
  const { accepted, accepted_version: version, accepted_at: at } = terms
  if (accepted && version && at) {
    return <Timed at={at}>{PROVISIONAL_COPY.termsAccepted.render({ version, time: when(at), email })}</Timed>
  }
  if (!accepted && version && at) {
    return <Timed at={at}>{PROVISIONAL_COPY.termsNewVersion.render({ acceptedVersion: version, time: when(at) })}</Timed>
  }
  return copyText('termsNotAccepted')
}

export function TermsCard({ terms, email }: TermsCardProps) {
  const [open, setOpen] = useState(false)
  const [acceptedHere, setAcceptedHere] = useState(false)
  const status = statusLine(terms, email)

  return (
    <Card data-terms-card="">
      <CardHeader>
        <h2 className="text-h3 text-navy-950">{copyText('termsCardTitle')}</h2>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {acceptedHere && terms.accepted ? (
          <Band kind="success">
            <p>{status}</p>
          </Band>
        ) : (
          <p className="text-body-sm text-navy-950">{status}</p>
        )}
        <div data-terms-summary="" className="flex flex-col gap-2">
          <h3 className="text-body-sm font-medium text-navy-950">{copyText('termsSummaryHeading')}</h3>
          {PLACEHOLDER ? (
            <p data-legal-placeholder="" className="text-caption text-[var(--color-warning-800)]">
              {LEGAL_Q14_TAG}
            </p>
          ) : null}
          <ul data-warranties="" className="list-disc pl-5 text-body-sm text-navy-700">
            {PROCESSING_TERMS.warranties.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
        <div>
          <Button variant={terms.accepted ? 'secondary' : 'primary'} onClick={() => setOpen(true)}>
            {copyText('termsAccept')}
          </Button>
        </div>
        <ProcessingTermsDialog
          open={open}
          onAccepted={() => {
            setOpen(false)
            setAcceptedHere(true)
          }}
          onCancel={() => setOpen(false)}
        />
      </CardContent>
    </Card>
  )
}
